import { SewingPinFilledIcon, StarFilledIcon, StarIcon } from '@radix-ui/react-icons';

import ThumbnailWithFallback from '~/components/photos/thumbnail-with-fallback';
import { Icon } from '~/components/ui/icon';
import { type GymSearchResult } from '~/domain/gyms/models/gym';
import { THUMBNAIL_SIZE } from '~/domain/shared/models/thumbnail-size';
import { cn } from '~/styles/extended-theme.ts';
import { type PropsWithClassName } from '~/utils/props-with-classname';

import { GymCardFavoriteButton } from '../resources+/favorite';

import { renderAverageRating } from './ratings';

interface Props {
  gym: GymSearchResult;
  onFavoriteChange?: (gymId: string, isFavorite: boolean) => void;
}

const CARD_CONTENT_CLASS_NAME =
  'w-full max-h-12 whitespace-break-spaces break-all overflow-ellipsis line-clamp-2';

const renderAddress = (gym: GymSearchResult) => {
  return (
    <div className={cn(CARD_CONTENT_CLASS_NAME, 'text-body1')}>
      <div className="capitalize">{gym.addressLine1}</div>
      {gym.addressLine2 ? <div className="capitalize">{gym.addressLine2}</div> : null}
      <div>
        {gym.city}, {gym.stateAbbreviation} {gym.zip}
      </div>
    </div>
  );
};

const renderNeighborhood = (gym: GymSearchResult) => {
  return <div className={cn(CARD_CONTENT_CLASS_NAME, 'text-body1')}>{gym.neighborhood}</div>;
};

export function GymCard({ className, gym, onFavoriteChange }: PropsWithClassName<Props>) {
  return (
    <div className={cn('px-8 py-4 shadow-md tablet:px-4 desktop:w-[408px]', className)}>
      <div
        className={cn(
          'grid h-full grid-cols-[auto_1fr_0.75rem_6rem] gap-2',
          gym.tagline ? 'grid-rows-[repeat(3,_auto)_1fr]' : 'grid-rows-[repeat(2,_auto)_1fr]',
        )}
      >
        <GymCardFavoriteButton gymId={gym.id} onChange={onFavoriteChange} />
        <div className="col-start-2">
          <h1 className="bold text-body1 font-bold">
            <div className={CARD_CONTENT_CLASS_NAME}>
              {gym.name}
              {gym.verified ? (
                <>
                  &nbsp;
                  <Icon
                    name="verified"
                    className="mb-[2px]" // For better visual alignment
                  />
                </>
              ) : null}
            </div>
          </h1>
        </div>
        {gym.tagline ? (
          <div className="col-start-2">
            <div className={CARD_CONTENT_CLASS_NAME}>
              <h2 className="text-body1 italic">{gym.tagline}</h2>
            </div>
          </div>
        ) : null}

        <SewingPinFilledIcon className="col-start-1 h-6 w-6" />
        {gym.neighborhood ? renderNeighborhood(gym) : renderAddress(gym)}

        {gym.numberOfRatings === 0 ? (
          <>
            <StarIcon className="col-start-1 h-6 w-6 text-primary-light" />
            <p className="text-body1">Not rated</p>
          </>
        ) : (
          <>
            <StarFilledIcon className="col-start-1 h-6 w-6 text-accent" />
            <p className="text-body1">
              {renderAverageRating(gym.averageRating)} ({gym.numberOfRatings})
            </p>
          </>
        )}

        <div className={cn('col-start-4 row-start-1', gym.tagline ? 'row-span-4' : 'row-span-3')}>
          <ThumbnailWithFallback
            size={THUMBNAIL_SIZE.SQUARE_EXTRA_SMALL}
            url={gym.thumbnailUrl ?? '/img/fp_placeholder_gymlist.png'}
            alt=""
            rounded
          />
        </div>
      </div>
    </div>
  );
}
